import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  ArrowLeft,
  Send,
  Loader2,
  CheckCircle,
  Mail,
  MapPin
} from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, form);
      setSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F8F7F4]" data-testid="contact-page">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#4B6651] hover:underline mb-6">
            <ArrowLeft className="w-4 h-4" />
            Back to App
          </Link>
          <Card className="border-[#E5E4E0]">
            <CardContent className="p-8 sm:p-12 text-center" data-testid="contact-success">
              <div className="w-16 h-16 rounded-full bg-[#4B6651]/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-[#4B6651]" />
              </div>
              <h2 className="text-xl font-semibold text-[#2A2C2A] font-['Work_Sans'] mb-2">
                Message Sent
              </h2>
              <p className="text-[#5C5F5C] mb-6">
                Thank you for reaching out. We'll get back to you within 1-2 business days.
              </p>
              <Button
                onClick={() => { setSubmitted(false); setForm({ name: '', email: '', subject: '', message: '' }); }}
                variant="outline"
                className="border-[#4B6651] text-[#4B6651] hover:bg-[#4B6651] hover:text-white"
                data-testid="send-another-btn"
              >
                Send Another Message
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F7F4]" data-testid="contact-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#4B6651] hover:underline mb-6">
          <ArrowLeft className="w-4 h-4" />
          Back to App
        </Link>

        <h1 className="text-2xl sm:text-3xl font-semibold text-[#2A2C2A] font-['Work_Sans'] mb-2">
          Contact Us
        </h1>
        <p className="text-[#5C5F5C] mb-8">
          Have a question, found a bug, or need support? We're here to help.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          <div className="flex items-center gap-3 bg-white border border-[#E5E4E0] rounded-lg p-4">
            <div className="w-10 h-10 rounded-lg bg-[#4B6651]/10 flex items-center justify-center flex-shrink-0">
              <Mail className="w-5 h-5 text-[#4B6651]" />
            </div>
            <div>
              <p className="text-xs text-[#5C5F5C]">Email</p>
              <a href="mailto:helpme@vetneurohelp.com" className="text-sm font-medium text-[#2A2C2A] hover:text-[#4B6651]" data-testid="contact-email-link">
                helpme@vetneurohelp.com
              </a>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-white border border-[#E5E4E0] rounded-lg p-4">
            <div className="w-10 h-10 rounded-lg bg-[#4B6651]/10 flex items-center justify-center flex-shrink-0">
              <MapPin className="w-5 h-5 text-[#4B6651]" />
            </div>
            <div>
              <p className="text-xs text-[#5C5F5C]">Location</p>
              <p className="text-sm font-medium text-[#2A2C2A]">Ellijay, GA</p>
            </div>
          </div>
        </div>

        <Card className="border-[#E5E4E0]">
          <CardHeader>
            <CardTitle className="text-lg font-['Work_Sans']">Send a Message</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label htmlFor="contact-name" className="text-sm font-medium text-[#2A2C2A]">Name</label>
                  <Input
                    id="contact-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Dr. Jane Smith"
                    required
                    className="bg-[#F8F7F4] border-[#E5E4E0] focus:ring-[#4B6651]/30 focus:border-[#4B6651]"
                    data-testid="contact-name"
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="contact-email" className="text-sm font-medium text-[#2A2C2A]">Email</label>
                  <Input
                    id="contact-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="you@example.com"
                    required
                    className="bg-[#F8F7F4] border-[#E5E4E0] focus:ring-[#4B6651]/30 focus:border-[#4B6651]"
                    data-testid="contact-email"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label htmlFor="contact-subject" className="text-sm font-medium text-[#2A2C2A]">Subject</label>
                <Input
                  id="contact-subject"
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  placeholder="Bug report, feature request, or general question"
                  required
                  className="bg-[#F8F7F4] border-[#E5E4E0] focus:ring-[#4B6651]/30 focus:border-[#4B6651]"
                  data-testid="contact-subject"
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="contact-message" className="text-sm font-medium text-[#2A2C2A]">Message</label>
                <Textarea
                  id="contact-message"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Describe your issue or question in detail..."
                  required
                  rows={6}
                  className="bg-[#F8F7F4] border-[#E5E4E0] focus:ring-[#4B6651]/30 focus:border-[#4B6651] resize-none"
                  data-testid="contact-message"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#4B6651] hover:bg-[#3E5543] text-white"
                data-testid="contact-submit"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Send Message
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="mt-12 pt-6 pb-12 border-t border-[#E5E4E0] flex flex-wrap gap-4 text-sm text-[#5C5F5C]">
          <Link to="/terms" className="hover:text-[#4B6651] hover:underline">Terms of Service</Link>
          <Link to="/privacy" className="hover:text-[#4B6651] hover:underline">Privacy Policy</Link>
        </div>
      </div>
    </div>
  );
};

export default Contact;
